import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import * as React from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';

export interface HorizontalCarouselProps {
  /**
   * The items to display in the carousel. Each item should be a React component.
   */
  items: React.ReactNode[];
  /**
   * The number of items visible at one time. Defaults to 3.
   */
  numVisibleItems?: number;
  /**
   * Gap between items in pixels. Defaults to 16.
   */
  gap?: number;
  /**
   * Custom styles for the carousel container
   */
  sx?: SxProps<Theme>;
  /**
   * Custom styles for individual items
   */
  itemSx?: SxProps<Theme>;
}

export function HorizontalCarousel({
  items,
  numVisibleItems = 3,
  gap = 16,
  sx,
  itemSx,
}: HorizontalCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate if we need navigation
  const needsNavigation = items.length > numVisibleItems;
  const maxIndex = Math.max(0, items.length - numVisibleItems);

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  // Calculate the transform offset
  const itemWidth = `calc((100% - ${(numVisibleItems - 1) * gap}px) / ${numVisibleItems})`;
  const translateX = `calc(-${currentIndex} * (${itemWidth} + ${gap}px))`;

  // Calculate display range
  const startIndex = currentIndex + 1;
  const endIndex = Math.min(currentIndex + numVisibleItems, items.length);
  const displayText = `Displaying ${startIndex}-${endIndex} of ${items.length}`;

  return (
    <Stack
      direction="column"
      sx={{
        width: '100%',
        gap: 2,
        ...sx,
      }}
    >
      {/* Carousel Content */}
      <Stack
        direction="row"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Stack
          ref={containerRef}
          direction="row"
          sx={{
            transition: 'transform 0.3s ease-in-out',
            transform: `translateX(${translateX})`,
            gap: `${gap}px`,
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: `0 0 ${itemWidth}`,
                height: '100%',
                ...itemSx,
              }}
            >
              {item}
            </Box>
          ))}
        </Stack>
      </Stack>

      {/* Navigation and Display Count */}
      {needsNavigation && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body-xs">{displayText}</Typography>
          <Stack direction="row" sx={{ gap: 0 }}>
            <IconButton
              variant="transparent"
              size="sm"
              icon={ChevronLeftIcon}
              disabled={currentIndex === 0}
              onClick={handlePrevious}
              ariaLabel="Previous items"
            />
            <IconButton
              variant="transparent"
              size="sm"
              icon={ChevronRightIcon}
              disabled={currentIndex >= maxIndex}
              onClick={handleNext}
              ariaLabel="Next items"
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
